import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Banknote, Loader2, Users, Wallet, Star, Sparkles, Lock, User, Building2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

// PIN Input Component for Kids
const PinInput = ({ value, onChange, length = 4 }) => {
  const handleChange = (index, digit) => {
    if (!/^\d*$/.test(digit)) return; // Only allow digits
    
    const newPin = value.split('');
    newPin[index] = digit;
    const newValue = newPin.join('').slice(0, length);
    onChange(newValue);
    
    // Auto-focus next input
    if (digit && index < length - 1) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  return (
    <div className="flex gap-2 sm:gap-3 justify-center">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          id={`pin-${index}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className="w-12 h-14 sm:w-14 sm:h-16 text-center text-xl sm:text-2xl font-bold rounded-xl sm:rounded-2xl border-3 border-purple-300 
                     bg-white/80 focus:border-purple-500 focus:ring-4 focus:ring-purple-200 
                     transition-all outline-none shadow-lg"
          data-testid={`pin-input-${index}`}
        />
      ))}
    </div>
  );
};

// Banker Portal Component
const BankerPortal = ({ loading, setLoading, login, register, navigate }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    username: '', 
    password: '', 
    signupType: 'new', // 'new' or 'join'
    bankName: '',
    bankerCode: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginData.username, loginData.password);
      if (user.role !== 'banker') {
        toast.error('Please use the Customer portal to login');
        return;
      }
      toast.success(`Welcome back, ${user.username}!`);
      navigate('/banker');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate based on signup type
    if (registerData.signupType === 'new' && !registerData.bankName.trim()) {
      toast.error('Please enter a Bank Name');
      return;
    }
    if (registerData.signupType === 'join' && !registerData.bankerCode.trim()) {
      toast.error('Please enter a Banker Code');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        username: registerData.username,
        password: registerData.password,
        role: 'banker',
        bank_name: registerData.signupType === 'new' ? registerData.bankName : null,
        banker_code: registerData.signupType === 'join' ? registerData.bankerCode : null
      };
      
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, payload);
      const { user, access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success(`Welcome to TinyBank, ${user.username}!`);
      navigate('/banker');
      window.location.reload(); // Refresh to update auth context
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md shadow-xl border-slate-200 bg-white" data-testid="banker-auth-card">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-slate-100">
          <TabsTrigger value="login" data-testid="banker-login-tab">Login</TabsTrigger>
          <TabsTrigger value="register" data-testid="banker-register-tab">Sign Up</TabsTrigger>
        </TabsList>
        
        <TabsContent value="login">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Users className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-xl">Banker Login</CardTitle>
            <CardDescription>Access your parent dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="banker-username">Username</Label>
                <Input
                  id="banker-username"
                  data-testid="banker-login-username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  placeholder="Enter your username"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="banker-password">Password</Label>
                <Input
                  id="banker-password"
                  data-testid="banker-login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 h-11"
                disabled={loading}
                data-testid="banker-login-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Sign In
              </Button>
            </form>
          </CardContent>
        </TabsContent>
        
        <TabsContent value="register">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Users className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-xl">Create Banker Account</CardTitle>
            <CardDescription>Start managing your family's finances</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="banker-reg-username">Username</Label>
                <Input
                  id="banker-reg-username"
                  data-testid="banker-register-username"
                  value={registerData.username}
                  onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                  placeholder="Choose a username"
                  required
                  minLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="banker-reg-password">Password</Label>
                <Input
                  id="banker-reg-password"
                  data-testid="banker-register-password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Create a password (min 8 characters)"
                  required
                  minLength={8}
                />
              </div>
              
              {/* Signup Type Toggle */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">How do you want to get started?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRegisterData({ ...registerData, signupType: 'new', bankerCode: '' })}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      registerData.signupType === 'new'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="signup-type-new"
                  >
                    <Building2 className={`w-5 h-5 mb-1 ${registerData.signupType === 'new' ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <div className={`text-sm font-medium ${registerData.signupType === 'new' ? 'text-emerald-700' : 'text-slate-600'}`}>
                      Create New Bank
                    </div>
                    <div className="text-xs text-slate-500">Start fresh</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegisterData({ ...registerData, signupType: 'join', bankName: '' })}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      registerData.signupType === 'join'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="signup-type-join"
                  >
                    <UserPlus className={`w-5 h-5 mb-1 ${registerData.signupType === 'join' ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <div className={`text-sm font-medium ${registerData.signupType === 'join' ? 'text-emerald-700' : 'text-slate-600'}`}>
                      Join Existing Bank
                    </div>
                    <div className="text-xs text-slate-500">Have a code?</div>
                  </button>
                </div>
              </div>
              
              {/* Bank Name or Banker Code based on signup type */}
              {registerData.signupType === 'new' ? (
                <div className="space-y-2">
                  <Label htmlFor="bank-name" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-600" />
                    Bank Name
                  </Label>
                  <Input
                    id="bank-name"
                    data-testid="banker-bank-name"
                    value={registerData.bankName}
                    onChange={(e) => setRegisterData({ ...registerData, bankName: e.target.value })}
                    placeholder="e.g., The Smith Family Bank"
                    required
                    minLength={2}
                  />
                  <p className="text-xs text-slate-500">This is what your family bank will be called</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="banker-code" className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-emerald-600" />
                    Banker Code
                  </Label>
                  <Input
                    id="banker-code"
                    data-testid="banker-code-input"
                    value={registerData.bankerCode}
                    onChange={(e) => setRegisterData({ ...registerData, bankerCode: e.target.value })}
                    placeholder="Paste the banker code here"
                    required
                  />
                  <p className="text-xs text-slate-500">Ask the existing banker for their Banker ID</p>
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 h-11"
                disabled={loading}
                data-testid="banker-register-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {registerData.signupType === 'new' ? 'Create Bank' : 'Join Bank'}
              </Button>
            </form>
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
};

// Customer Portal Component (Kid-Friendly)
const CustomerPortal = ({ loading, setLoading, login, register, navigate }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ username: '', pin: '' });
  const [registerData, setRegisterData] = useState({ username: '', pin: '', linkedBankerId: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginData.pin.length !== 4) {
      toast.error('Please enter your 4-digit PIN');
      return;
    }
    setLoading(true);
    try {
      const user = await login(loginData.username, loginData.pin);
      if (user.role !== 'customer') {
        toast.error('Please use the Banker portal to login');
        return;
      }
      toast.success(`Welcome back, ${user.username}! 🎉`);
      navigate('/customer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.pin.length !== 4) {
      toast.error('Please enter a 4-digit PIN');
      return;
    }
    setLoading(true);
    try {
      const user = await register(
        registerData.username, 
        registerData.pin, 
        'customer', 
        registerData.linkedBankerId || null
      );
      toast.success(`Welcome to TinyBank, ${user.username}! 🎉`);
      navigate('/customer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Fun floating elements for kids
  const FloatingElements = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-10 left-10 text-4xl animate-bounce" style={{ animationDelay: '0s' }}>⭐</div>
      <div className="absolute top-20 right-16 text-3xl animate-bounce" style={{ animationDelay: '0.5s' }}>🌟</div>
      <div className="absolute bottom-32 left-16 text-4xl animate-bounce" style={{ animationDelay: '1s' }}>💰</div>
      <div className="absolute bottom-20 right-10 text-3xl animate-bounce" style={{ animationDelay: '1.5s' }}>🐷</div>
      <div className="absolute top-1/2 left-8 text-2xl animate-bounce" style={{ animationDelay: '0.7s' }}>✨</div>
      <div className="absolute top-1/3 right-8 text-2xl animate-bounce" style={{ animationDelay: '1.2s' }}>🎯</div>
    </div>
  );

  return (
    <div className="relative">
      <FloatingElements />
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden" data-testid="customer-auth-card">
        {/* Colorful Header */}
        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 p-6 text-center">
          <div className="w-20 h-20 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-xl">
            <span className="text-4xl">🐷</span>
          </div>
          <h2 className="text-2xl font-bold text-white drop-shadow-lg">Kids Zone!</h2>
          <p className="text-white/90 text-sm mt-1">Your pocket money adventure starts here</p>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-none h-14">
            <TabsTrigger 
              value="login" 
              className="data-[state=active]:bg-white data-[state=active]:text-purple-600 text-purple-500 font-semibold rounded-xl m-1"
              data-testid="customer-login-tab"
            >
              🔑 Login
            </TabsTrigger>
            <TabsTrigger 
              value="register" 
              className="data-[state=active]:bg-white data-[state=active]:text-pink-600 text-pink-500 font-semibold rounded-xl m-1"
              data-testid="customer-register-tab"
            >
              ✨ Sign Up
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="login" className="p-6 pt-4">
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="kid-username" className="text-purple-700 font-semibold flex items-center gap-2">
                  <User className="w-4 h-4" /> Your Name
                </Label>
                <Input
                  id="kid-username"
                  data-testid="customer-login-username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  placeholder="Type your name here..."
                  className="h-12 rounded-xl border-2 border-purple-200 focus:border-purple-400 text-lg"
                  required
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-purple-700 font-semibold flex items-center gap-2 justify-center">
                  <Lock className="w-4 h-4" /> Enter Your Secret PIN
                </Label>
                <PinInput 
                  value={loginData.pin} 
                  onChange={(pin) => setLoginData({ ...loginData, pin })}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 
                         hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transition-all"
                disabled={loading}
                data-testid="customer-login-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <span className="mr-2">🚀</span>
                )}
                Let's Go!
              </Button>
            </form>
          </TabsContent>
          
          <TabsContent value="register" className="p-6 pt-4">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="kid-reg-username" className="text-pink-700 font-semibold flex items-center gap-2">
                  <Star className="w-4 h-4" /> Pick a Cool Name
                </Label>
                <Input
                  id="kid-reg-username"
                  data-testid="customer-register-username"
                  value={registerData.username}
                  onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                  placeholder="What should we call you?"
                  className="h-12 rounded-xl border-2 border-pink-200 focus:border-pink-400 text-lg"
                  required
                  minLength={3}
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-pink-700 font-semibold flex items-center gap-2 justify-center">
                  <Lock className="w-4 h-4" /> Create Your Secret PIN
                </Label>
                <PinInput 
                  value={registerData.pin} 
                  onChange={(pin) => setRegisterData({ ...registerData, pin })}
                />
                <p className="text-center text-sm text-pink-500">Pick 4 numbers you can remember!</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="kid-banker-id" className="text-pink-700 font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" /> Parent's Code (ask them!)
                </Label>
                <Input
                  id="kid-banker-id"
                  data-testid="customer-banker-id"
                  value={registerData.linkedBankerId}
                  onChange={(e) => setRegisterData({ ...registerData, linkedBankerId: e.target.value })}
                  placeholder="Paste the code from your parent"
                  className="h-11 rounded-xl border-2 border-pink-200 focus:border-pink-400"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-pink-500 to-orange-400 
                         hover:from-pink-600 hover:to-orange-500 shadow-lg hover:shadow-xl transition-all"
                disabled={loading}
                data-testid="customer-register-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <span className="mr-2">🎉</span>
                )}
                Start Saving!
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

// Main Auth Page
const AuthPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [portalType, setPortalType] = useState('customer'); // 'banker' or 'customer'

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-4 sm:p-8 transition-all duration-500 ${
      portalType === 'banker' 
        ? 'bg-gradient-to-br from-emerald-50 via-white to-slate-50' 
        : 'bg-gradient-to-br from-purple-100 via-pink-50 to-orange-100'
    }`}>
      {/* Logo & Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-all duration-500 ${
          portalType === 'banker'
            ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
            : 'bg-gradient-to-br from-purple-400 to-pink-500'
        }`}>
          <Banknote className="w-7 h-7 text-white" />
        </div>
        <h1 className={`text-3xl font-bold bg-clip-text text-transparent transition-all duration-500 ${
          portalType === 'banker'
            ? 'bg-gradient-to-r from-emerald-600 to-teal-600'
            : 'bg-gradient-to-r from-purple-600 to-pink-600'
        }`}>
          TinyBank
        </h1>
      </div>

      {/* Portal Toggle */}
      <div className="mb-6 p-1.5 bg-white/80 backdrop-blur rounded-2xl shadow-lg" data-testid="portal-toggle">
        <div className="flex gap-1">
          <button
            onClick={() => setPortalType('banker')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
              portalType === 'banker'
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
            data-testid="toggle-banker"
          >
            <Users className="w-4 h-4" />
            <span>Parent</span>
          </button>
          <button
            onClick={() => setPortalType('customer')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
              portalType === 'customer'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
            data-testid="toggle-customer"
          >
            <Wallet className="w-4 h-4" />
            <span>Kid</span>
          </button>
        </div>
      </div>

      {/* Portal Content */}
      <div className="w-full max-w-md">
        {portalType === 'banker' ? (
          <BankerPortal 
            loading={loading} 
            setLoading={setLoading} 
            login={login} 
            register={register} 
            navigate={navigate} 
          />
        ) : (
          <CustomerPortal 
            loading={loading} 
            setLoading={setLoading} 
            login={login} 
            register={register} 
            navigate={navigate} 
          />
        )}
      </div>

      {/* Footer */}
      <p className={`mt-6 text-sm transition-colors duration-500 ${
        portalType === 'banker' ? 'text-slate-500' : 'text-purple-400'
      }`}>
        {portalType === 'banker' ? 'Banker Portal' : 'Kids Portal'} • TinyBank
      </p>
      
      {/* Discreet Admin Link */}
      <button
        onClick={() => navigate('/super-admin')}
        className="mt-4 text-xs text-slate-400/50 hover:text-slate-400 transition-colors"
        data-testid="admin-link"
      >
        Admin
      </button>
    </div>
  );
};

export default AuthPage;
